import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@repo/api-client";
import type { CommunityStory } from "@repo/shared-types";

export default function StoryDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [story, setStory] = useState<CommunityStory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      setLoading(true);
      try {
        const nextStory = await api.getStory(slug);
        setStory(nextStory);
      } catch {
        setError("Story not found.");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <div className="cap-page"><div className="container" style={{ padding: "6rem 0", textAlign: "center" }}>Loading…</div></div>;
  if (error || !story) return <div className="cap-page"><div className="container" style={{ padding: "6rem 0", textAlign: "center", color: "#b45309" }}>{error || "Not found"}</div></div>;

  return (
    <div className="cap-page">
      <section className="cap-hero">
        <div className="container">
          <Link to="/stories" className="btn btn--ghost" style={{ marginBottom: "1rem" }}>← All stories</Link>
          <div className="cap-hero__shell cap-hero__shell--split">
            <div>
              <span className="cap-hero__badge">{story.category.replace("-", " ")}</span>
              <h1 className="display-1 cap-hero__title">{story.title}</h1>
              <p className="cap-hero__subtitle">By {story.author_name} · {story.read_time_minutes} min read</p>
              {story.published_at && (
                <p className="text-muted body-sm">Published {new Date(story.published_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
              )}
              {story.bio && <p className="cap-hero__subtitle" style={{ fontStyle: "italic" }}>{story.bio}</p>}
            </div>

            <aside className="cap-hero__aside">
              <div className="cap-hero__card" style={{ display: "grid", gap: "1rem" }}>
                {story.featured_image_url ? (
                  <img src={story.featured_image_url} alt={story.title} style={{ width: "100%", height: "16rem", objectFit: "cover", borderRadius: "1rem" }} />
                ) : (
                  <div className="cap-card__media" style={{ minHeight: "16rem", borderRadius: "1rem" }}>💛</div>
                )}
                <div className="cap-card__meta-row">
                  <span className="cap-pill cap-pill--gold">{story.like_count} likes</span>
                  <span className="cap-pill cap-pill--indigo">{story.tags.length} tags</span>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="section section--surface-alt">
        <div className="container">
          <div className="cap-layout" style={{ alignItems: "start" }}>
            <section className="cap-panel">
              <div className="cap-panel__header"><h2 className="heading-lg">Story</h2></div>
              <div className="cap-panel__body">
                <div style={{ display: "grid", gap: "1rem" }}>
                  {story.body.split("\n\n").map((paragraph) => (
                    <p key={paragraph} className="body-md" style={{ color: "var(--c-ink-75)", lineHeight: 1.9, whiteSpace: "pre-wrap" }}>
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </section>

            <aside className="cap-panel">
              <div className="cap-panel__header"><h2 className="heading-lg">Details</h2></div>
              <div className="cap-panel__body">
                <div className="cap-list">
                  <div className="cap-list__item"><span className="cap-list__index">1</span><span className="body-sm">{story.category.replace("-", " ")}</span></div>
                  <div className="cap-list__item"><span className="cap-list__index">2</span><span className="body-sm">{story.read_time_minutes} minute read</span></div>
                  <div className="cap-list__item"><span className="cap-list__index">3</span><span className="body-sm">{story.like_count} likes</span></div>
                  {story.tags.map((tag) => (
                    <div key={tag} className="cap-list__item"><span className="cap-list__index">#</span><span className="body-sm">{tag}</span></div>
                  ))}
                </div>
                <div className="hero-actions" style={{ marginTop: "1.5rem" }}>
                  <Link to="/stories" className="btn btn--outline">Back to stories</Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
